import { useCallback, useState } from 'react';
import { generateId, type CanvasJSON, type SavedProject } from '@doer/frontend-components/creativity-editor';
import { useProjectStorage } from './use-project-storage';

interface UseProjectActionsOptions {
  width: number;
  height: number;
}

export function useProjectActions(options: UseProjectActionsOptions) {
  const { saveProject, updateProject, getProject, projects } = useProjectStorage();
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);

  const { width, height } = options;

  const handleSaveProject = useCallback(
    (canvasJSON: CanvasJSON) => {
      if (currentProjectId) {
        updateProject(currentProjectId, { canvasJSON });
        return;
      }
      const project: SavedProject = { id: generateId(), canvasJSON, width, height };
      saveProject(project);
      setCurrentProjectId(project.id);
    },
    [currentProjectId, width, height, saveProject, updateProject]
  );

  const handleSaveAsNewProject = useCallback(
    (canvasJSON: CanvasJSON) => {
      const project: SavedProject = { id: generateId(), canvasJSON, width, height };
      saveProject(project);
      setCurrentProjectId(project.id);
    },
    [width, height, saveProject]
  );

  const handleSelectProject = useCallback(
    (projectId: string) => {
      const project = getProject(projectId);
      if (!project) return;
      setCurrentProjectId(projectId);
      return project;
    },
    [getProject]
  );

  return {
    handleSaveProject,
    handleSaveAsNewProject,
    handleSelectProject,
    currentProjectId,
    projects
  };
}
